import { useEffect, useState } from "react";
import axios from "axios";
import {
  Clock,
  RefreshCw,
  AlertTriangle,
  MapPin,
  Info,
  Activity,
  CalendarRange,
} from "lucide-react";
import { LocationIndicator } from "./LocationIndicator";
import { TensionCard } from "./TensionCard";
import { ForecastCategoryCard } from "./ForecastCategoryCard";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section B de la Veille Proactive — Épidémiologie locale + forecast 7 jours.
 *
 * Deux sous-blocs séparés par un divider Cabinet subtil :
 * - B1 : pathologies actives dans le département (Sentinelles + SPF + Météo)
 * - B2 : forecast 7 jours par catégorie thérapeutique
 *
 * Branchée sur 2 endpoints (étape 4) appelés en parallèle :
 * - GET /api/veille-proactive/epidemiologie-locale → indicators
 * - GET /api/veille-proactive/forecast-saisonnier  → categories
 *
 * Les 2 partagent la même structure meta (location, sources, missing_sources).
 * On utilise la meta du forecast (la plus riche).
 *
 * Charte Cabinet stricte. Aucun spinner basique. Skeleton premium.
 */
export function EpidemiologieSection({ onCompleteProfile }) {
  const [loading, setLoading] = useState(true);
  const [epidemio, setEpidemio] = useState(null);
  const [forecast, setForecast] = useState(null);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem("benchory_token");
      const headers = { Authorization: `Bearer ${token}` };
      const [respE, respF] = await Promise.all([
        axios.get(`${API_URL}/veille-proactive/epidemiologie-locale`, {
          headers,
          timeout: 15000,
        }),
        axios.get(`${API_URL}/veille-proactive/forecast-saisonnier`, {
          headers,
          timeout: 15000,
        }),
      ]);
      setEpidemio(respE.data);
      setForecast(respF.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[veille-proactive] Epidemiologie fetch error:", e?.message);
      setEpidemio({
        data: { location: {}, indicators: [], forecast_meteo: [] },
        meta: { status: "unavailable" },
      });
      setForecast({
        data: { location: {}, categories: [] },
        meta: { status: "unavailable" },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const meta = forecast?.meta || epidemio?.meta || {};
  const location = forecast?.data?.location || epidemio?.data?.location || {};
  const indicators = epidemio?.data?.indicators || [];
  const categories = forecast?.data?.categories || [];

  return (
    <section
      className="space-y-6"
      data-testid="epidemiologie-section"
      aria-labelledby="epidemiologie-title"
    >
      <SectionHeader
        meta={meta}
        location={location}
        loading={loading}
        onRefresh={() => {
          setLoading(true);
          fetchAll();
        }}
      />

      {loading && <EpidemioSkeleton />}

      {!loading && (
        <EpidemioContent
          indicators={indicators}
          categories={categories}
          meta={meta}
          location={location}
          onCompleteProfile={onCompleteProfile}
        />
      )}
    </section>
  );
}


// ── En-tête ───────────────────────────────────────────────────────────────────


function SectionHeader({ meta, location, loading, onRefresh }) {
  const isStale = _isOld(meta?.cached_at);
  const missing = meta?.missing_sources || [];

  return (
    <header className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2
            id="epidemiologie-title"
            className="font-playfair text-2xl lg:text-3xl text-cabinet-ink font-medium tracking-tight"
          >
            Épidémiologie locale et forecast 7 jours
          </h2>
          <p className="mt-1.5 text-cabinet-navy text-[14px] leading-relaxed max-w-2xl">
            Indicateurs composites pour votre zone et projection de la demande
            par catégorie thérapeutique sur les prochains 7 jours.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex-shrink-0 text-cabinet-mute-1 hover:text-cabinet-bordeaux transition-colors p-2 rounded-md disabled:opacity-40"
          data-testid="epidemiologie-refresh"
          aria-label="Actualiser les indicateurs épidémiologiques"
        >
          <RefreshCw size={16} strokeWidth={1.8} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <LocationIndicator location={location} />
        {meta?.weights && (
          <WeightsTooltip weights={meta.weights} />
        )}
        {meta?.cached_at && (
          <span
            className="inline-flex items-center gap-1.5 text-[11.5px] tracking-wide text-cabinet-mute-1"
            data-testid="cached-at-indicator"
          >
            <Clock size={11} strokeWidth={1.8} />
            {isStale ? "Données du" : "Mis à jour"} {_formatDateTime(meta.cached_at)}
          </span>
        )}
      </div>

      {missing.length > 0 && meta?.user_message && (
        <div
          className="inline-flex items-start gap-2 px-3.5 py-2 rounded-md bg-cabinet-cream-warm border border-cabinet-gold/30 text-[12.5px] text-cabinet-ink max-w-2xl"
          data-testid="partial-sources-banner"
        >
          <Info size={13} className="flex-shrink-0 mt-0.5 text-cabinet-gold" strokeWidth={1.8} />
          <span>{meta.user_message}</span>
        </div>
      )}
    </header>
  );
}


function WeightsTooltip({ weights }) {
  const lines = Object.entries(weights || {})
    .map(([k, v]) => `${_sourceLabel(k)} : ${Math.round(v * 100)} %`)
    .join("\n");
  return (
    <span
      className="inline-flex items-center gap-1 text-[11.5px] tracking-wide text-cabinet-mute-1 cursor-help"
      title={`Pondération de l'indicateur composite :\n${lines}`}
      data-testid="weights-tooltip"
    >
      <Info size={11} strokeWidth={1.8} />
      Pondération composite
    </span>
  );
}


function _sourceLabel(key) {
  return (
    { sentinelles: "Sentinelles", spf: "Santé Publique France", meteo_france: "Météo France" }[key] ||
    key
  );
}


// ── Skeleton ──────────────────────────────────────────────────────────────────


function EpidemioSkeleton() {
  return (
    <div className="space-y-10" data-testid="epidemiologie-skeleton">
      {/* B1 skeleton : 3 tension cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="bg-white border border-cabinet-mute-2/40 rounded-lg p-5 animate-pulse"
          >
            <div className="h-5 w-2/5 rounded bg-cabinet-mute-2/50 mb-2" />
            <div className="h-3 w-1/3 rounded bg-cabinet-mute-2/30 mb-4" />
            <div className="flex gap-1.5">
              {[0, 1, 2, 3].map((s) => (
                <div key={s} className="h-1.5 flex-1 rounded-sm bg-cabinet-mute-2/30" />
              ))}
            </div>
            <div className="h-3 w-3/4 rounded bg-cabinet-mute-2/25 mt-4" />
          </div>
        ))}
      </div>
      {/* Divider */}
      <div className="h-px bg-cabinet-mute-2/40" />
      {/* B2 skeleton : 4 forecast cards */}
      <div className="space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white border border-cabinet-mute-2/40 rounded-lg p-5 animate-pulse"
          >
            <div className="h-5 w-2/5 rounded bg-cabinet-mute-2/50 mb-3" />
            <div className="flex gap-1.5 mb-4">
              {[0, 1, 2, 3].map((s) => (
                <div key={s} className="h-1.5 flex-1 rounded-sm bg-cabinet-mute-2/30" />
              ))}
            </div>
            <div className="h-3 w-4/5 rounded bg-cabinet-mute-2/25" />
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Contenu (5 états) ────────────────────────────────────────────────────────


function EpidemioContent({ indicators, categories, meta, location, onCompleteProfile }) {
  const status = meta?.status;
  const isUnavailable = status === "unavailable";
  const isEmpty = status === "empty" || !location?.dept;
  const hasIndicators = indicators.length > 0;
  const hasCategories = categories.length > 0;

  if (isUnavailable && !hasIndicators && !hasCategories) {
    return (
      <PremiumEmptyState
        icon={AlertTriangle}
        title="Les indicateurs épidémiologiques sont momentanément indisponibles"
        message="Nos trois sources (Réseau Sentinelles, Santé Publique France, Météo France) restent surveillées en continu. Les analyses principales de la Veille Proactive ne sont pas affectées."
        testId="epidemio-empty-unavailable"
      />
    );
  }

  if (isEmpty || !location?.dept) {
    return (
      <PremiumEmptyState
        icon={MapPin}
        title="Renseignez votre adresse pour activer la veille épidémiologique locale"
        message="Vos indicateurs régionaux sont calculés depuis le code postal de votre pharmacie. Quelques secondes suffisent pour activer cette section."
        testId="epidemio-empty-no-location"
        ctaLabel="Compléter mon profil"
        onCtaClick={onCompleteProfile}
      />
    );
  }

  return (
    <div className="space-y-10">
      {/* Sous-bloc B1 — Pathologies actives */}
      <SubBlock
        kicker="Situation actuelle"
        title="Pathologies actives dans votre zone"
        icon={Activity}
        testId="subblock-b1"
      >
        {hasIndicators ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {indicators.map((ind) => (
              <TensionCard key={ind.pathologie} indicator={ind} />
            ))}
          </div>
        ) : (
          <InlineEmpty message="Aucune pathologie en alerte dans votre zone cette semaine." />
        )}
      </SubBlock>

      <div className="h-px bg-cabinet-mute-2/40" aria-hidden />

      {/* Sous-bloc B2 — Forecast 7 jours */}
      <SubBlock
        kicker="Projection 7 jours"
        title="Demande projetée par catégorie thérapeutique"
        icon={CalendarRange}
        testId="subblock-b2"
      >
        {hasCategories ? (
          <div className="space-y-3">
            {categories.map((cat) => (
              <ForecastCategoryCard key={cat.name} category={cat} />
            ))}
          </div>
        ) : (
          <InlineEmpty message="Pas assez de données pour projeter la demande sur 7 jours." />
        )}
      </SubBlock>
    </div>
  );
}


function SubBlock({ kicker, title, icon: Icon, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className="text-cabinet-bordeaux/70" strokeWidth={1.7} />
        <span className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-cabinet-mute-1">
          {kicker}
        </span>
      </div>
      <h3 className="font-playfair text-xl text-cabinet-ink font-medium tracking-tight mb-5">
        {title}
      </h3>
      {children}
    </div>
  );
}


function InlineEmpty({ message }) {
  return (
    <p className="text-[13.5px] text-cabinet-mute-1 italic font-light px-4 py-6 text-center bg-cabinet-cream-warm border border-cabinet-mute-2/30 rounded-md">
      {message}
    </p>
  );
}


function PremiumEmptyState({ icon: Icon, title, message, testId, ctaLabel, onCtaClick }) {
  return (
    <div
      className="bg-cabinet-cream-warm border border-cabinet-mute-2/40 rounded-lg px-8 py-12 text-center"
      data-testid={testId}
    >
      <div className="inline-flex w-12 h-12 rounded-full bg-cabinet-bordeaux/8 items-center justify-center mb-4">
        <Icon size={20} className="text-cabinet-bordeaux" strokeWidth={1.5} />
      </div>
      <h3 className="font-playfair text-xl text-cabinet-ink font-medium tracking-tight">
        {title}
      </h3>
      <p className="mt-2.5 text-cabinet-navy text-[14px] leading-relaxed max-w-md mx-auto">
        {message}
      </p>
      {ctaLabel && onCtaClick && (
        <button
          onClick={onCtaClick}
          className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-cabinet-bordeaux hover:text-cabinet-bordeaux-soft tracking-wide transition-colors"
          data-testid="epidemio-empty-cta"
        >
          {ctaLabel}
          <span aria-hidden>→</span>
        </button>
      )}
    </div>
  );
}


// ── Helpers ───────────────────────────────────────────────────────────────────


function _formatDateTime(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const datePart = new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
    }).format(d);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${datePart} à ${hh}h${mm}`;
  } catch {
    return iso;
  }
}


function _isOld(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return false;
    return (Date.now() - d.getTime()) / (1000 * 60 * 60) > 24;
  } catch {
    return false;
  }
}


export default EpidemiologieSection;
